import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { API } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { Layers, Satellite, Cpu, Bot, Link2, Eye, ArrowRight } from "lucide-react";

const STEPS = [
  { key: "cube", icon: Layers, color: "#00FFFF" },
  { key: "sat", icon: Satellite, color: "#00FF7F" },
  { key: "q", icon: Cpu, color: "#8A2BE2" },
  { key: "ai", icon: Bot, color: "#F59E0B" },
  { key: "chain", icon: Link2, color: "#00FFFF" },
];

const useCountUp = (target, ms = 1400) => {
  const [v, setV] = useState(0);
  useEffect(() => {
    if (target == null) return;
    let raf, start;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / ms);
      setV(target * (1 - Math.pow(1 - p, 3)));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, ms]);
  return v;
};

const fmt = (n, decimals = 0) =>
  n >= 1e6 ? `${(n / 1e6).toFixed(2)}M` : n >= 1e4 ? `${(n / 1e3).toFixed(1)}k` : n.toLocaleString(undefined, { maximumFractionDigits: decimals });

const Counter = ({ value, label, accent, decimals = 0, testId }) => {
  const v = useCountUp(value);
  return (
    <div className="border-l border-white/10 pl-4 py-1" data-testid={testId}>
      <div className="font-mono text-2xl md:text-3xl font-bold tabular-nums" style={{ color: accent }}>{value == null ? "—" : fmt(v, decimals)}</div>
      <div className="text-[10px] uppercase tracking-widest text-slate-500 mt-1">{label}</div>
    </div>
  );
};

export const DataLayer = () => {
  const { t } = useLang();
  const [s, setS] = useState(null);
  const [safety, setSafety] = useState(null);

  useEffect(() => {
    const load = () => { axios.get(`${API}/stats/public`).then((r) => setS(r.data)).catch(() => {}); axios.get(`${API}/renewal/city-safety`).then((r) => setSafety(r.data)).catch(() => {}); };
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="data-layer" className="relative mx-auto max-w-7xl px-6 py-24 overflow-hidden" data-testid="data-layer-section">
      <div className="absolute -right-32 -top-32 h-96 w-96 rounded-full border border-[#00FFFF]/10 qs-orbit pointer-events-none" />
      <div className="absolute -right-16 -top-16 h-64 w-64 rounded-full border border-[#8A2BE2]/15 qs-orbit-rev pointer-events-none" />

      <div className="grid lg:grid-cols-5 gap-10 items-end">
        <div className="lg:col-span-3">
          <span className="inline-flex items-center gap-2 text-[#00FF7F] text-xs font-mono uppercase tracking-widest">
            <Eye className="h-4 w-4" /> {t("data_tag")}
          </span>
          <h2 className="font-display font-bold text-3xl md:text-5xl tracking-tight mt-4">{t("data_title")}</h2>
          <p className="text-slate-400 mt-4 max-w-2xl leading-relaxed">{t("data_sub")}</p>
        </div>
        <div className="lg:col-span-2 flex items-center gap-2 text-xs font-mono text-slate-500 lg:justify-end">
          <span className="relative flex h-2 w-2"><span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#00FF7F] opacity-75" /><span className="relative inline-flex h-2 w-2 rounded-full bg-[#00FF7F]" /></span>
          {t("data_live")}
        </div>
      </div>

      {/* pipeline */}
      <div className="relative mt-14 grid grid-cols-1 md:grid-cols-5 gap-3" data-testid="data-pipeline">
        <div className="hidden md:block absolute top-9 left-[10%] right-[10%] h-px qs-flow" />
        {STEPS.map((st, i) => (
          <motion.div key={st.key}
            initial={{ opacity: 0, y: 18 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.12 }}
            className="relative qs-panel p-5 qs-lift" data-testid={`pipeline-step-${st.key}`}>
            <div className="flex items-center justify-between">
              <span className="grid place-items-center h-9 w-9" style={{ background: `${st.color}1A`, boxShadow: `0 0 24px ${st.color}22` }}>
                <st.icon className="h-5 w-5" style={{ color: st.color }} />
              </span>
              <span className="font-mono text-[10px] text-slate-600">0{i + 1}</span>
            </div>
            <h4 className="font-display font-semibold mt-4">{t(`step_${st.key}`)}</h4>
            <p className="text-xs text-slate-500 mt-1.5 leading-relaxed">{t(`step_${st.key}_d`)}</p>
            {i < STEPS.length - 1 && <ArrowRight className="hidden md:block absolute -right-3.5 top-8 h-4 w-4 text-slate-700" />}
          </motion.div>
        ))}
      </div>

      {/* live counters */}
      <div className="mt-12 grid grid-cols-2 md:grid-cols-5 gap-x-6 gap-y-8" data-testid="live-counters">
        <Counter testId="counter-samples" value={s?.spectral_samples} label={t("m_samples")} accent="#00FFFF" />
        <Counter testId="counter-scenes" value={s?.scenes_searched} label={t("m_scenes")} accent="#00FF7F" />
        <Counter testId="counter-scans" value={s?.scans} label={t("m_scans")} accent="#F8FAFC" />
        <Counter testId="counter-quantum" value={s?.quantum_runs} label={t("m_quantum")} accent="#8A2BE2" />
        <Counter testId="counter-answers" value={s?.analyst_answers} label={t("m_answers")} accent="#F59E0B" />
        <Counter testId="counter-certs" value={s?.certificates} label={t("m_certs")} accent="#00FFFF" />
        <Counter testId="counter-credits" value={s?.carbon_credits} label={t("m_credits")} accent="#00FF7F" decimals={1} />
        <Counter testId="counter-alerts" value={s?.alerts} label={t("m_alerts")} accent="#EF4444" />
        <Counter testId="counter-lots" value={s?.kg_farm_to_fork} label={t("m_lots")} accent="#00FF7F" decimals={0} />
        <Counter testId="counter-tenders" value={s?.tenders_assessed} label={t("m_tenders")} accent="#F59E0B" />
      </div>
      {safety && safety.cities.length > 0 && (
        <div className="mt-10" data-testid="city-safety">
          <div className="flex flex-wrap items-baseline justify-between gap-2 mb-3">
            <h3 className="font-display font-semibold text-base md:text-lg">{t("cs_title")}</h3>
            <span className="text-[11px] font-mono text-slate-500">{safety.totals.renewed_units} {t("cs_renewed")} · {safety.totals.high_risk_units_retired} {t("cs_retired")} · {safety.totals.high_risk_units_open} {t("cs_open")} · {Math.round(safety.totals.bond_capital_try / 1e6)} M TRY {t("cs_bonds")} · {safety.totals.tco2e_yr} tCO2e/yr</span>
          </div>
          <div className="flex gap-2 overflow-x-auto pb-2">
            {safety.cities.map((c) => (
              <a key={c.city} href="/renewal" className="qs-panel p-3 min-w-[180px] shrink-0 hover:border-[#F59E0B]/50" data-testid={`city-safety-${c.city}`}>
                <div className="font-display font-semibold">{c.city}</div>
                <div className="font-mono text-2xl font-bold text-[#00FF7F]">{c.renewed_units}<span className="text-[10px] text-slate-500 font-normal"> {t("cs_renewed")}</span></div>
                <div className="text-[10px] font-mono text-slate-400">{c.high_risk_units_retired} {t("cs_retired")} · {c.high_risk_units_open} {t("cs_open")}</div>
                <div className="text-[10px] font-mono text-slate-500">{Math.round(c.bond_capital_try / 1e6)} M TRY {t("cs_bonds")} · {c.retrofit} retrofit / {c.renewal} renewal</div>
              </a>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};
